import { supabase } from '../lib/supabase'
import {
  ChatRoom,
  ChatMessage,
  ChatMessageInput,
  ChatMessageQueryOptions,
  ChatMessagePage,
  ChatRoomInput,
  ChatRoomMember,
  ChatRoomMemberWithProfile,
} from '../types/chat'

export class ChatService {
  /**
   * Get the default (global) chat room
   */
  static async getDefaultRoom(): Promise<ChatRoom> {
    const { data, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .eq('is_default', true)
      .single()

    if (error) {
      throw new Error(`Default chat room not found: ${error.message}`)
    }

    if (!data) {
      throw new Error('Default chat room not found. Please run the chat migration.')
    }

    return data
  }

  /**
   * Get a room by ID
   */
  static async getRoomById(roomId: string): Promise<ChatRoom | null> {
    const { data, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .eq('id', roomId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw new Error(`Failed to fetch room: ${error.message}`)
    }

    return data
  }

  /**
   * Send a message to a room
   */
  static async sendMessage(input: ChatMessageInput): Promise<ChatMessage> {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Must be authenticated to send messages')
    }

    const trimmedContent = input.content.trim()
    if (!trimmedContent) {
      throw new Error('Message content cannot be empty')
    }

    if (trimmedContent.length > 4000) {
      throw new Error('Message content exceeds maximum length of 4000 characters')
    }

    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        room_id: input.room_id,
        user_id: user.id,
        content: trimmedContent,
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to send message: ${error.message}`)
    }

    return data
  }

  /**
   * Get messages for a room with cursor-based pagination
   * Returns messages in chronological order (oldest first)
   */
  static async getMessages(
    roomId: string,
    options: ChatMessageQueryOptions = {}
  ): Promise<ChatMessagePage> {
    const limit = options.limit || 50

    let query = supabase
      .from('chat_messages')
      .select('*')
      .eq('room_id', roomId)
      .order('created_at', { ascending: false })
      .limit(limit + 1) // Fetch one extra to check if more exist

    // Cursor-based pagination: get messages before this timestamp
    if (options.before) {
      query = query.lt('created_at', options.before)
    }

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to fetch messages: ${error.message}`)
    }

    const hasMore = (data?.length || 0) > limit
    const messages = hasMore ? data!.slice(0, limit) : (data || [])

    // Enrich with author names
    const enrichedMessages = await this.enrichMessagesWithAuthors(messages)

    // Return in chronological order for display
    return {
      messages: enrichedMessages.reverse(),
      hasMore,
    }
  }

  /**
   * Search messages using full-text search
   */
  static async searchMessages(
    roomId: string,
    query: string,
    limit: number = 50
  ): Promise<ChatMessage[]> {
    const { data, error } = await supabase.rpc('search_chat_messages', {
      p_room_id: roomId,
      p_query: query,
      p_limit: limit,
    })

    if (error) {
      throw new Error(`Search failed: ${error.message}`)
    }

    return this.enrichMessagesWithAuthors(data || [])
  }

  /**
   * Search messages using partial text matching (ilike).
   * Unlike full-text search, this supports partial string matches
   * (e.g. typing "hel" will match "hello"), making it suitable
   * for search-as-you-type.
   */
  static async searchMessagesPartial(
    roomId: string,
    query: string,
    limit: number = 50
  ): Promise<ChatMessage[]> {
    const trimmed = query.trim()
    if (!trimmed) return []

    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('room_id', roomId)
      .ilike('content', `%${trimmed}%`)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      throw new Error(`Search failed: ${error.message}`)
    }

    // Return in chronological order for display
    const messages = (data || []).reverse()
    return this.enrichMessagesWithAuthors(messages)
  }

  /**
   * Edit a message (user can edit own within 30 min, admins can edit any)
   */
  static async editMessage(messageId: string, content: string): Promise<ChatMessage> {
    const trimmedContent = content.trim()
    if (!trimmedContent) {
      throw new Error('Message content cannot be empty')
    }

    if (trimmedContent.length > 4000) {
      throw new Error('Message content exceeds maximum length of 4000 characters')
    }

    const { data, error } = await supabase
      .from('chat_messages')
      .update({ content: trimmedContent })
      .eq('id', messageId)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to edit message: ${error.message}`)
    }

    return data
  }

  /**
   * Delete a message (user can only delete their own within 30 min, admins can delete any)
   */
  static async deleteMessage(messageId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_messages')
      .delete()
      .eq('id', messageId)

    if (error) {
      throw new Error(`Failed to delete message: ${error.message}`)
    }
  }

  /**
   * Enrich messages with author display names from profiles
   */
  private static async enrichMessagesWithAuthors(
    messages: ChatMessage[]
  ): Promise<ChatMessage[]> {
    if (messages.length === 0) return []

    const userIds = [...new Set(messages.map((m) => m.user_id))]

    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name')
      .in('id', userIds)

    const profileMap = new Map(
      profiles?.map((p) => [p.id, p.display_name]) || []
    )

    return messages.map((m) => ({
      ...m,
      author_name: profileMap.get(m.user_id) || 'Unknown User',
    }))
  }

  // ============================================================================
  // ADMIN METHODS - Room Management
  // ============================================================================

  /**
   * Get all chat rooms (admin only)
   */
  static async getAllRooms(): Promise<ChatRoom[]> {
    const { data, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .order('is_default', { ascending: false })
      .order('name', { ascending: true })

    if (error) {
      throw new Error(`Failed to fetch rooms: ${error.message}`)
    }

    return data || []
  }

  /**
   * Create a new chat room (admin only)
   */
  static async createRoom(input: ChatRoomInput): Promise<ChatRoom> {
    const trimmedName = input.name.trim()
    if (!trimmedName) {
      throw new Error('Room name cannot be empty')
    }

    const { data, error } = await supabase
      .from('chat_rooms')
      .insert({
        name: trimmedName,
        description: input.description?.trim() || null,
        is_default: false,
      })
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to create room: ${error.message}`)
    }

    return data
  }

  /**
   * Update a chat room (admin only)
   */
  static async updateRoom(roomId: string, input: Partial<ChatRoomInput>): Promise<ChatRoom> {
    const updates: Record<string, unknown> = {}

    if (input.name !== undefined) {
      const trimmedName = input.name.trim()
      if (!trimmedName) {
        throw new Error('Room name cannot be empty')
      }
      updates.name = trimmedName
    }

    if (input.description !== undefined) {
      updates.description = input.description?.trim() || null
    }

    const { data, error } = await supabase
      .from('chat_rooms')
      .update(updates)
      .eq('id', roomId)
      .select()
      .single()

    if (error) {
      throw new Error(`Failed to update room: ${error.message}`)
    }

    return data
  }

  /**
   * Delete a chat room (admin only, cannot delete default room)
   */
  static async deleteRoom(roomId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_rooms')
      .delete()
      .eq('id', roomId)
      .eq('is_default', false) // Safety check

    if (error) {
      throw new Error(`Failed to delete room: ${error.message}`)
    }
  }

  /**
   * Get members of a room with profile info
   */
  static async getRoomMembers(roomId: string): Promise<ChatRoomMemberWithProfile[]> {
    const { data: members, error: membersError } = await supabase
      .from('chat_room_members')
      .select('*')
      .eq('room_id', roomId)
      .order('joined_at', { ascending: true })

    if (membersError) {
      throw new Error(`Failed to fetch room members: ${membersError.message}`)
    }

    if (!members || members.length === 0) return []

    // Get profile info for all members
    const userIds = members.map((m) => m.user_id)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, account_type')
      .in('id', userIds)

    const profileMap = new Map(
      profiles?.map((p) => [p.id, { display_name: p.display_name, account_type: p.account_type }]) || []
    )

    return members.map((m) => ({
      ...m,
      display_name: profileMap.get(m.user_id)?.display_name || null,
      account_type: profileMap.get(m.user_id)?.account_type || null,
    }))
  }

  /**
   * Add a user to a room (admin only)
   */
  static async addMemberToRoom(roomId: string, userId: string): Promise<ChatRoomMember> {
    const { data, error } = await supabase
      .from('chat_room_members')
      .insert({
        room_id: roomId,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      if (error.code === '23505') {
        throw new Error('User is already a member of this room')
      }
      throw new Error(`Failed to add member: ${error.message}`)
    }

    return data
  }

  /**
   * Remove a user from a room (admin only)
   */
  static async removeMemberFromRoom(roomId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('chat_room_members')
      .delete()
      .eq('room_id', roomId)
      .eq('user_id', userId)

    if (error) {
      throw new Error(`Failed to remove member: ${error.message}`)
    }
  }

  /**
   * Get all users for member selection (admin only)
   */
  static async getAllUsers(): Promise<{ id: string; display_name: string | null; account_type: string | null }[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, account_type')
      .order('display_name', { ascending: true })

    if (error) {
      throw new Error(`Failed to fetch users: ${error.message}`)
    }

    return data || []
  }

  /**
   * Get rooms the current user has access to
   */
  static async getUserRooms(): Promise<ChatRoom[]> {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Must be authenticated to get rooms')
    }

    // Get default rooms and rooms user is a member of
    const { data: rooms, error } = await supabase
      .from('chat_rooms')
      .select('*')
      .order('is_default', { ascending: false })
      .order('name', { ascending: true })

    if (error) {
      throw new Error(`Failed to fetch rooms: ${error.message}`)
    }

    // Filter to rooms user has access to
    const { data: memberships } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', user.id)

    const memberRoomIds = new Set(memberships?.map((m) => m.room_id) || [])

    return (rooms || []).filter((room) => room.is_default || memberRoomIds.has(room.id))
  }

  // ============================================================================
  // UNREAD MESSAGE TRACKING
  // ============================================================================

  /**
   * Get unread message counts for all rooms the user has access to
   */
  static async getUnreadCounts(): Promise<Map<string, number>> {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      return new Map()
    }

    const { data, error } = await supabase.rpc('get_unread_counts', {
      p_user_id: user.id,
    })

    if (error) {
      console.error('Failed to get unread counts:', error)
      return new Map()
    }

    const counts = new Map<string, number>()
    for (const row of data || []) {
      counts.set(row.room_id, row.unread_count)
    }

    return counts
  }

  /**
   * Mark a room as read (update last_read_at to now)
   */
  static async markRoomAsRead(roomId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('Must be authenticated to mark room as read')
    }

    const { error } = await supabase
      .from('chat_read_status')
      .upsert(
        {
          user_id: user.id,
          room_id: roomId,
          last_read_at: new Date().toISOString(),
        },
        {
          onConflict: 'user_id,room_id',
        }
      )

    if (error) {
      console.error('Failed to mark room as read:', error)
    }
  }

  /**
   * Get the last read timestamp for a specific room
   */
  static async getLastReadAt(roomId: string): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      return null
    }

    const { data, error } = await supabase
      .from('chat_read_status')
      .select('last_read_at')
      .eq('user_id', user.id)
      .eq('room_id', roomId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      console.error('Failed to get last read at:', error)
      return null
    }

    return data?.last_read_at || null
  }
}

